import store from "@/store";
import { transformText } from "./textUtils";
import ptOverrides from "@/i18n/ptOverrides";
import esOverrides from "@/i18n/esOverrides";
import curriculumOverrides from "@/i18n/curriculumOverrides";
import companyProfileOverrides from "@/i18n/companyProfileOverrides.mjs";
import subscriptionOverrides from "@/i18n/subscriptionOverrides.mjs";
import referenceOverrides from "@/i18n/referenceOverrides.mjs";
import candidateManagementOverrides from "@/i18n/candidateManagementOverrides.mjs";
import applicationFunnelOverrides from "@/i18n/applicationFunnelOverrides.mjs";
import moderationReportsOverrides from "@/i18n/moderationReportsOverrides.mjs";
import notificationsOverrides from "@/i18n/notificationsOverrides.mjs";
import candidateDashboardOverrides from "@/i18n/candidateDashboardOverrides.mjs";
import jobInvitationsOverrides from "@/i18n/jobInvitationsOverrides.mjs";
import applicationListOverrides from "@/i18n/applicationListOverrides.mjs";
import savedJobsOverrides from "@/i18n/savedJobsOverrides.mjs";
import professionalInterviewsOverrides from "@/i18n/professionalInterviewsOverrides.mjs";
import employerInterviewsOverrides from "@/i18n/employerInterviewsOverrides.mjs";
import employerJobsOverrides from "@/i18n/employerJobsOverrides.mjs";
import employerJobDetailOverrides from "@/i18n/employerJobDetailOverrides.mjs";
import publicationWindowOverrides from "@/i18n/publicationWindowOverrides.mjs";
import jobEditorActionOverrides from "@/i18n/jobEditorActionOverrides.mjs";
import { reactive } from 'vue';

const translationsByLanguage = reactive(new Map());
const loadedCatalogues = new Set();

function normalizeLanguage(language) {
    const selectedLang = language || 'en';
    return selectedLang.toLowerCase().split('-')[0];
}

function mergeOverrides(language, translations) {
    if (language === 'pt')
        translations = { ...translations, ...ptOverrides };
    if (language === 'es')
        translations = { ...translations, ...esOverrides };
    return { ...translations, ...(curriculumOverrides[language] || {}), ...(companyProfileOverrides[language] || {}), ...(subscriptionOverrides[language] || {}), ...(referenceOverrides[language] || {}), ...(candidateManagementOverrides[language] || {}), ...(applicationFunnelOverrides[language] || {}), ...(moderationReportsOverrides[language] || {}), ...(notificationsOverrides[language] || {}), ...(candidateDashboardOverrides[language] || {}), ...(jobInvitationsOverrides[language] || {}), ...(applicationListOverrides[language] || {}), ...(savedJobsOverrides[language] || {}), ...(professionalInterviewsOverrides[language] || {}), ...(employerInterviewsOverrides[language] || {}), ...(employerJobsOverrides[language] || {}), ...(employerJobDetailOverrides[language] || {}), ...(publicationWindowOverrides[language] || {}), ...(jobEditorActionOverrides[language] || {}) };
}

export async function loadTranslations(language = store.getters.getUserLang || 'en', { requireCatalogue = false } = {}) {
    const useLang = normalizeLanguage(language);
    if (translationsByLanguage.has(useLang) && (!requireCatalogue || loadedCatalogues.has(useLang))) {
        return translationsByLanguage.get(useLang);
    }

    let translations = {};
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    try {
        const response = await fetch(`/data/lang/${useLang}.json`, {
            signal: controller.signal
        });
        if (response.ok) {
            translations = await response.json();
            loadedCatalogues.add(useLang);
        } else if (requireCatalogue) {
            throw new Error('Language catalogue unavailable');
        }
    } catch (error) {
        if (requireCatalogue) throw error;
        // The interface remains usable with its source strings when offline.
    } finally {
        window.clearTimeout(timeout);
    }

    translations = mergeOverrides(useLang, translations);
    translationsByLanguage.set(useLang, translations);
    store.dispatch('saveTranslations', translations);
    return translations;
}

export function getAllTranslations() {
    const useLang = normalizeLanguage(store.getters.getUserLang || 'en');
    if (translationsByLanguage.has(useLang)) {
        return translationsByLanguage.get(useLang);
    }

    return {};
}

export function translate(text = '', textTransform = '') {
    if(!text || text == '')
        return text;
    try {
        const translations = getAllTranslations();
        const normalizedText = typeof text === 'string' ? text.toLowerCase() : text;
        const translationKey = translations[text] !== undefined ? text : normalizedText;
        if(translations[translationKey] === undefined || translations[translationKey] === null || translations[translationKey] === '')
            return transformText(text, textTransform);
        return translations[translationKey] === undefined || translations[translationKey] === null
                ? transformText(text, textTransform)
                : transformText(translations[translationKey], textTransform);
    } catch (error) {
        console.error(error);
        return text;
    }
}
